const SUBTOTAL_PREFIX = 'subtotal::'

function getColumnGroupKey(column) {
  return (
    String(column?.stage_type || '').trim()
    || String(column?.stage_name || '').trim()
    || String(column?.stage_id || '').trim()
    || 'Без типа'
  )
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

function firstNumber(...values) {
  for (const value of values) {
    const number = Number(value)
    if (Number.isFinite(number)) return number
  }
  return 0
}

function getColumnPlanBase(column) {
  return firstNumber(
    column?.amount_without_vat,
    column?.plan_after_tax,
    column?.plan_net,
    column?.plan,
  )
}

function getColumnFactBase(column) {
  return firstNumber(
    column?.acted_amount_after_tax,
    column?.fact_after_tax,
    column?.fact_net,
    column?.fact,
  )
}

function getColumnPlanGross(column) {
  return firstNumber(column?.plan, column?.amount_with_vat)
}

function getColumnFactGross(column) {
  return firstNumber(column?.fact, column?.acted_amount)
}

function normalizeSubTeamValues(row, columnsById) {
  if (row?.row_type !== 'sub-team' || row?.category !== 'team_income') return { ...(row?.values || {}) }

  const values = { ...(row.values || {}) }
  Object.entries(values).forEach(([columnId, value]) => {
    const column = columnsById.get(columnId)
    if (!column || column.is_total) return
    const base = getColumnPlanBase(column)
    if (!base) return
    values[columnId] = {
      ...(value || {}),
      percent: roundMoney(((Number(value?.plan) || 0) / base) * 100),
    }
  })
  return values
}

function getTaxSubtotalPercent(ids, values, columnsById, groupPlanBase) {
  if (!groupPlanBase) return 0
  const weightedPercent = ids.reduce((sum, id) => {
    const base = getColumnPlanBase(columnsById.get(String(id)))
    const percent = Number(values[id]?.percent)
    return Number.isFinite(percent) ? sum + base * percent : sum
  }, 0)
  return roundMoney(weightedPercent / groupPlanBase)
}

/**
 * Преобразует таблицу бюджета объекта: бюджеты группируются по типу стадии,
 * после бюджетов каждого типа добавляется колонка-итог («итог по типу»),
 * а колонка «Итого по объекту» остаётся в самом конце.
 */
export function buildGroupedObjectBudgetTable(table) {
  if (!table?.columns?.length) return table

  const totalColumn = table.columns.find((column) => column.is_total) || null
  const budgetColumns = table.columns.filter((column) => !column.is_total)
  if (!budgetColumns.length) return table
  const columnsById = new Map(table.columns.map((column) => [String(column.id), column]))

  const groupOrder = []
  const groupColumns = new Map()
  budgetColumns.forEach((column) => {
    const key = getColumnGroupKey(column)
    if (!groupColumns.has(key)) {
      groupColumns.set(key, [])
      groupOrder.push(key)
    }
    groupColumns.get(key).push(column)
  })

  const subtotalMeta = new Map()
  const columns = []
  groupOrder.forEach((key) => {
    const groupCols = groupColumns.get(key)
    const groupPlanGross = roundMoney(groupCols.reduce((sum, column) => sum + getColumnPlanGross(column), 0))
    const groupFactGross = roundMoney(groupCols.reduce((sum, column) => sum + getColumnFactGross(column), 0))
    const groupPlanBase = roundMoney(groupCols.reduce((sum, column) => sum + getColumnPlanBase(column), 0))
    const groupFactBase = roundMoney(groupCols.reduce((sum, column) => sum + getColumnFactBase(column), 0))
    const subtotalId = `${SUBTOTAL_PREFIX}${key}`
    subtotalMeta.set(subtotalId, {
      ids: groupCols.map((column) => column.id),
      groupPlanGross,
      groupFactGross,
      groupPlanBase,
      groupFactBase,
    })

    groupCols.forEach((column) => {
      columns.push({ ...column, group_type: key })
    })
    columns.push({
      id: subtotalId,
      label: key,
      sub_label: 'итог по типу',
      is_subtotal: true,
      group_type: key,
      plan: groupPlanGross,
      fact: groupFactGross,
      amount_without_vat: groupPlanBase,
      acted_amount_after_tax: groupFactBase,
    })
  })

  if (totalColumn) {
    columns.push({ ...totalColumn, group_type: null })
  }

  const rows = table.rows.map((row) => {
    const values = normalizeSubTeamValues(row, columnsById)
    subtotalMeta.forEach(({ ids, groupPlanGross, groupFactGross, groupPlanBase, groupFactBase }, subtotalId) => {
      const isTotalRow = Boolean(row.is_total)
      const isAfterTaxTotalRow = row.row_type === 'after-tax-total' || row.is_after_tax_total
      const plan = isTotalRow
        ? groupPlanGross
        : isAfterTaxTotalRow
          ? groupPlanBase
          : roundMoney(ids.reduce((sum, id) => sum + (Number(values[id]?.plan) || 0), 0))
      const fact = isTotalRow
        ? groupFactGross
        : isAfterTaxTotalRow
          ? groupFactBase
          : roundMoney(ids.reduce((sum, id) => sum + (Number(values[id]?.fact) || 0), 0))
      const subtotalPercent = isTotalRow || isAfterTaxTotalRow
        ? (plan ? 100 : 0)
        : row.category === 'tax'
          ? getTaxSubtotalPercent(ids, values, columnsById, groupPlanBase)
          : (groupPlanBase ? roundMoney((plan / groupPlanBase) * 100) : 0)
      values[subtotalId] = {
        plan,
        fact,
        percent: subtotalPercent,
      }
    })
    return { ...row, values }
  })

  return { ...table, columns, rows }
}

/**
 * Группирует колонки для меню показа/скрытия: бюджеты по типу стадии вместе
 * с их итогом, а «Итого по объекту» — отдельной группой.
 */
export function getObjectBudgetColumnGroups(columns = []) {
  const groups = []
  let currentKey
  let currentGroup = null

  columns.forEach((column) => {
    if (column.is_total) {
      groups.push({ key: 'total', label: 'Итого', columns: [column] })
      currentKey = undefined
      currentGroup = null
      return
    }
    const key = column.group_type || 'Без типа'
    if (!currentGroup || key !== currentKey) {
      currentKey = key
      currentGroup = { key, label: `Стадии (${key})`, columns: [] }
      groups.push(currentGroup)
    }
    currentGroup.columns.push(column)
  })

  return groups
}
